import { LayoutEnum } from '@goldcast/api/content';
import { isNumber } from 'radash';
import type { CSSProperties } from '../../types/css-properties';
import { SCREENSHARE_VIDEO_ID } from '../../constants/screenshare';
import { LayoutType, FitType } from '../../types/asset-types';
import { Clip } from '../../types/clip-types';
import { VideoElement } from '../../types/RemotionPlayerConfigTypes/Element/VideoElement';
import { Scene, VideoCropPosition } from '../../types/RemotionPlayerConfigTypes/Scene';
import { FacePositions, SpeakersAnalysis } from '../../types/speakers-analysis';
import { VideoFeed } from '../../types/video-feed-types';
import { approxLTE, isApproxWindowSubset } from '../../utils/compareUtils';
import { getScreenshareCropPositionMap } from './scenes';
import {
  buildSpeakerDetailsMap,
  buildVideoToSpeakerDetails,
  getSpeakerOverlaps,
  getVideoFeedSpeakerKey,
  isVideoFeedScreenshare,
  removeScreenshareFromVideoIdsVF,
  removeScreenshareWithoutSpeakerVF
} from './videoFeedAdapters';
import { BaseConfig } from '../../types/speaker-types';
import {
  isLayoutWithVisionDataEnabledPure,
  doesClipWorkOnAlignedStreams,
  isLiveEventRemotionWithPreviewAndAlignedStream,
  isClipAudiogram,
  isClipLayoutDefault,
  isNewLayoutsMigrated
} from '../../utils/clipUtils';
import { LayoutChange, VideoCropPositionStyle } from '../../types/RemotionPlayerConfigTypes/Segment/LayoutChange';
import { PlayRanges } from '../../types/RemotionPlayerConfigTypes/Segment/PlayRange';
import { getActiveSpeakersWithinTimeRange } from '../../utils/activeSpeaker';
import { getFitCropStyles, getCropStyles } from '../../utils/cropUtils';
import { isSpeakerLayout, isLayoutAudiogram, isLayoutDefault, getFinalConfigForLayout } from '../../utils/layout';
import { getLayoutConfigMap } from '../../utils/getLayoutConfig';
import { getAspectRatio } from '../../utils/sizeUtils';
import {
  getFlattenedDeletes,
  getAvailableRanges,
  getAdjustedFrame,
  mergeNegligibleLayoutChanges,
  mergeNegligiblePlayRanges
} from './utils';
import { CompositeSegment } from '../../types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import {
  DEFAULT_LAYOUT_TYPE,
  DEFAULT_SIZE_TYPE,
  DEFAULT_FIT_TYPE,
  DEFAULT_HIDE_BORDERS
} from '../../constants/clip-defaults';

export const getVideoCropPositionMapForUploadsVF = ({
  facePositions,
  speakerIdToFaceMap,
  selectedVisibleVideoIds,
  containerConfigsForAllNumberOfVideos,
  videoFeeds
}: {
  facePositions: FacePositions[];
  speakerIdToFaceMap: Record<string, number[]>;
  selectedVisibleVideoIds: string[];
  containerConfigsForAllNumberOfVideos: {
    [numberOfVideos: number]: BaseConfig[];
  };
  videoFeeds: VideoFeed[];
}):
  | {
      videoCropPositionMap: Record<string, VideoCropPosition>;
      faceBoundingBoxMap: Record<string, VideoCropPosition>;
      personBoundingBoxMap: Record<string, VideoCropPosition>;
      personContainerBoundingBoxMap: Record<string, VideoCropPosition>;
    }
  | undefined => {
  const speakerDetails = buildSpeakerDetailsMap(videoFeeds);
  const videoIdToSpeakerDetails = buildVideoToSpeakerDetails(videoFeeds);

  const speakerKeyToSpeakerIdMap = Object.keys(speakerDetails).reduce(
    (acc, speakerKey) => {
      acc[speakerKey] = speakerDetails[speakerKey].id;
      return acc;
    },
    {} as Record<string, string>
  );

  const videoIdToSpeakerKeyMap = Object.keys(videoIdToSpeakerDetails).reduce(
    (acc, videoId) => {
      acc[videoId] = videoIdToSpeakerDetails[videoId].speakerKey;
      return acc;
    },
    {} as Record<string, string>
  );

  const videoIdToFaceMap = Object.keys(videoIdToSpeakerKeyMap).reduce(
    (acc, videoId) => {
      const faceIdForVideoId = speakerIdToFaceMap?.[speakerKeyToSpeakerIdMap?.[videoIdToSpeakerKeyMap?.[videoId]]]?.[0];
      if (isNumber(faceIdForVideoId)) {
        acc[videoId] = faceIdForVideoId;
      }
      return acc;
    },
    {} as Record<string, number>
  );

  const selectedFaces = selectedVisibleVideoIds
    .map(videoId => videoIdToFaceMap[videoId])
    .filter(a => a !== undefined && a !== null);

  const filteredFacePositions = facePositions.filter(fp => selectedFaces.includes(fp.face_id));

  if (filteredFacePositions.length === 0) {
    return undefined;
  }

  const containerConfigForGivenVideos = containerConfigsForAllNumberOfVideos[selectedVisibleVideoIds.length];
  if (!containerConfigForGivenVideos) {
    return undefined;
  }

  const videoCropPositionMap: Record<string, VideoCropPosition> = {};
  const videoPersonBoundingBoxMap: Record<string, VideoCropPosition> = {};
  const videoFaceBoundingBoxMap: Record<string, VideoCropPosition> = {};
  const videoPersonContainerBoundingBoxMap: Record<string, VideoCropPosition> = {};

  selectedVisibleVideoIds.forEach((videoId, videoIndex) => {
    const speakerKey = videoIdToSpeakerKeyMap[videoId];
    if (!speakerKey) {
      return;
    }

    const speakerId = speakerKeyToSpeakerIdMap[speakerKey];
    if (!speakerId) {
      return;
    }

    const faceIds = speakerIdToFaceMap[speakerId];
    if (!faceIds || faceIds.length === 0) {
      videoCropPositionMap[videoId] = {
        top_left: { x: 0, y: 0 },
        bottom_right: { x: 1, y: 1 }
      };
      return;
    }

    const facePosition = filteredFacePositions.find(fp => faceIds.includes(fp.face_id));
    if (!facePosition) {
      return;
    }

    const containerConfig = containerConfigForGivenVideos[videoIndex];
    if (!containerConfig?.containerStyle) {
      return;
    }

    const faceBoundingBox = facePosition.crop_position_face || facePosition.crop_position;
    const personBoundingBox = facePosition.crop_position_person;
    const personContainerBoundingBox = facePosition.crop_position_person_container;

    if (!faceBoundingBox) {
      return;
    }

    let boundingBoxToExpand: VideoCropPosition;
    if (!personBoundingBox) {
      boundingBoxToExpand = faceBoundingBox;
    } else {
      videoPersonBoundingBoxMap[videoId] = personBoundingBox;
      boundingBoxToExpand = personBoundingBox;
    }
    videoFaceBoundingBoxMap[videoId] = faceBoundingBox;
    videoPersonBoundingBoxMap[videoId] = personBoundingBox || faceBoundingBox;
    if (personContainerBoundingBox) {
      videoPersonContainerBoundingBoxMap[videoId] = personContainerBoundingBox;
    }
    videoCropPositionMap[videoId] = boundingBoxToExpand;
  });

  return Object.keys(videoCropPositionMap).length > 0
    ? {
        videoCropPositionMap,
        faceBoundingBoxMap: videoFaceBoundingBoxMap,
        personBoundingBoxMap: videoPersonBoundingBoxMap,
        personContainerBoundingBoxMap: videoPersonContainerBoundingBoxMap
      }
    : undefined;
};

export const getCropPositionsForUploadsVF = ({
  clip,
  overlap,
  fps,
  finalLayout,
  fitMode,
  hideBorders,
  finalVideoIds,
  videos,
  hasVisibleVideoIds,
  videoFeeds,
  speakerAnalysis,
  isScreenshareDetectionEnabled,
  roundCorners = false,
  pipShape,
  pipPosition,
  isNewLayoutsEnabled = false
}: {
  clip: Clip;
  overlap: { fromFrame: number; toFrame: number; hasScreenshare: boolean };
  fps: number;
  finalLayout: Exclude<LayoutType, 'SPEAKER'>;
  fitMode: FitType;
  hideBorders: boolean;
  finalVideoIds: string[];
  videos: VideoElement[];
  hasVisibleVideoIds: boolean;
  videoFeeds: VideoFeed[];
  speakerAnalysis?: SpeakersAnalysis;
  isScreenshareDetectionEnabled: boolean;
  roundCorners?: boolean;
  pipShape?: LayoutChange['pipShape'];
  pipPosition?: LayoutChange['pipPosition'];
  isNewLayoutsEnabled?: boolean;
}):
  | {
      videoIds: string[];
      layoutForOverlap: Exclude<LayoutType, 'SPEAKER'>;
      videoCropPositionMap: Record<string, VideoCropPosition> | undefined;
      faceCoordinatesMap: Record<string, VideoCropPosition>;
      personContainerCoordinatesMap: Record<string, VideoCropPosition>;
    }
  | undefined => {
  const currentTime = overlap.fromFrame / fps;
  const containerConfigsForAllNumberOfVideos =
    getLayoutConfigMap({
      size: clip.asset_metadata.size,
      layout: finalLayout,
      fitMode,
      hideBorders,
      speakerDirection: clip.asset_metadata.speaker_direction,
      hasScreenshare: overlap.hasScreenshare,
      roundCorners,
      pipShape,
      pipPosition,
      isNewLayoutsEnabled
    }) ?? {};

  if (!speakerAnalysis || !speakerAnalysis.time_analysis || speakerAnalysis.time_analysis.length === 0) {
    return undefined;
  }

  const times = speakerAnalysis.times;
  const timeIndex = Math.max(
    times.findLastIndex(time => approxLTE(time, currentTime)),
    0
  );

  const timeAnalysisPoint = speakerAnalysis.time_analysis[timeIndex];
  const facePositions = timeAnalysisPoint.face_positions;

  const cropPositionMaps = getVideoCropPositionMapForUploadsVF({
    facePositions: structuredClone(facePositions),
    speakerIdToFaceMap: speakerAnalysis.speaker_mapping,
    selectedVisibleVideoIds: finalVideoIds,
    containerConfigsForAllNumberOfVideos,
    videoFeeds
  });
  let videoCropPositionMapForUploads = cropPositionMaps?.videoCropPositionMap;
  const personContainerBoundingBoxMap = cropPositionMaps?.personContainerBoundingBoxMap;

  const shouldIncludeScreenshare =
    isScreenshareDetectionEnabled &&
    speakerAnalysis?.screenshare_bounds &&
    speakerAnalysis.screenshare_bounds.length > 0 &&
    finalVideoIds.includes(SCREENSHARE_VIDEO_ID);

  if (shouldIncludeScreenshare) {
    const currentFrame = overlap.fromFrame;
    const screenshareCrops = getScreenshareCropPositionMap({
      frame: currentFrame,
      fps,
      speakersAnalysis: speakerAnalysis
    });
    if (screenshareCrops) {
      videoCropPositionMapForUploads = { ...screenshareCrops, ...videoCropPositionMapForUploads };
    }
  }

  const { faceBoundingBoxMap } =
    getAllVideoCropPositionMapForUploadsVF({
      facePositions: structuredClone(facePositions),
      speakerIdToFaceMap: speakerAnalysis.speaker_mapping,
      videoFeeds
    }) || {};

  const faceCoordinatesMap: Record<string, VideoCropPosition> = {};
  faceBoundingBoxMap &&
    Object.keys(faceBoundingBoxMap).forEach(videoId => {
      faceCoordinatesMap[videoId] = faceBoundingBoxMap?.[videoId];
    });

  const cropPositionVideoIds = Object.keys(videoCropPositionMapForUploads ?? {});

  const originalVideo = videos.find(v => v.isOriginalVideo);

  let videoIds = cropPositionVideoIds;
  if (facePositions.length === 0 && originalVideo) {
    videoIds = cropPositionVideoIds.length > 0 ? cropPositionVideoIds : [originalVideo.id];
  }
  let layoutForOverlap = finalLayout;

  if (!videoCropPositionMapForUploads && hasVisibleVideoIds && originalVideo) {
    videoIds = [originalVideo.id];
    layoutForOverlap = 'GRID';
  }

  if (videoIds.length <= 1) {
    layoutForOverlap = 'GRID';
  }

  return {
    videoIds,
    layoutForOverlap,
    videoCropPositionMap: videoCropPositionMapForUploads,
    faceCoordinatesMap,
    personContainerCoordinatesMap: personContainerBoundingBoxMap ?? {}
  };
};
export const getAllVideoCropPositionMapForUploadsVF = ({
  facePositions,
  speakerIdToFaceMap,
  videoFeeds
}: {
  facePositions: FacePositions[];
  speakerIdToFaceMap: Record<string, number[]>;
  videoFeeds: VideoFeed[];
}):
  | {
      videoCropPositionMap: Record<string, VideoCropPosition>;
      faceBoundingBoxMap: Record<string, VideoCropPosition>;
      personBoundingBoxMap: Record<string, VideoCropPosition>;
      personContainerBoundingBoxMap: Record<string, VideoCropPosition>;
    }
  | undefined => {
  const speakerDetails = buildSpeakerDetailsMap(videoFeeds);
  const videoIdToSpeakerDetails = buildVideoToSpeakerDetails(videoFeeds);

  const speakerKeyToSpeakerIdMap = Object.keys(speakerDetails).reduce(
    (acc, speakerKey) => {
      acc[speakerKey] = speakerDetails[speakerKey].id;
      return acc;
    },
    {} as Record<string, string>
  );

  const videoIdToSpeakerKeyMap = Object.keys(videoIdToSpeakerDetails).reduce(
    (acc, videoId) => {
      acc[videoId] = videoIdToSpeakerDetails[videoId].speakerKey;
      return acc;
    },
    {} as Record<string, string>
  );

  const videoCropPositionMap: Record<string, VideoCropPosition> = {};
  const videoPersonBoundingBoxMap: Record<string, VideoCropPosition> = {};
  const videoFaceBoundingBoxMap: Record<string, VideoCropPosition> = {};
  const videoPersonContainerBoundingBoxMap: Record<string, VideoCropPosition> = {};

  Object.keys(videoIdToSpeakerKeyMap).forEach(videoId => {
    const speakerKey = videoIdToSpeakerKeyMap[videoId];
    if (!speakerKey) {
      return;
    }

    const speakerId = speakerKeyToSpeakerIdMap[speakerKey];
    if (!speakerId) {
      return;
    }

    const faceIds = speakerIdToFaceMap[speakerId] || [];
    const facePosition = facePositions.find(fp => faceIds.includes(fp.face_id));
    if (!facePosition) {
      return;
    }

    const faceBoundingBox = facePosition.crop_position_face || facePosition.crop_position;
    const personBoundingBox = facePosition.crop_position_person;
    const personContainerBoundingBox = facePosition.crop_position_person_container;

    if (!faceBoundingBox) {
      return;
    }

    let boundingBoxToExpand: VideoCropPosition;
    if (!personBoundingBox) {
      boundingBoxToExpand = faceBoundingBox;
    } else {
      videoPersonBoundingBoxMap[videoId] = personBoundingBox;
      boundingBoxToExpand = personBoundingBox;
    }
    videoFaceBoundingBoxMap[videoId] = faceBoundingBox;
    videoPersonBoundingBoxMap[videoId] = personBoundingBox || faceBoundingBox;
    if (personContainerBoundingBox) {
      videoPersonContainerBoundingBoxMap[videoId] = personContainerBoundingBox;
    }
    videoCropPositionMap[videoId] = boundingBoxToExpand;
  });

  return Object.keys(videoCropPositionMap).length > 0
    ? {
        videoCropPositionMap,
        faceBoundingBoxMap: videoFaceBoundingBoxMap,
        personBoundingBoxMap: videoPersonBoundingBoxMap,
        personContainerBoundingBoxMap: videoPersonContainerBoundingBoxMap
      }
    : undefined;
};
export const getMutedVideoIdsVF = ({
  clip,
  videosInLayout,
  videos,
  isCreatedFromOriginalLayout,
  isFullFaceRecEnabled,
  isFullFaceRecForPreRecordedEnabled,
  isUploadsPreviewAudioDiscontinuityEnabled
}: {
  clip: Clip;
  videosInLayout: string[];
  videos: VideoElement[];
  isCreatedFromOriginalLayout: boolean;
  isFullFaceRecEnabled: boolean;
  isFullFaceRecForPreRecordedEnabled: boolean;
  isUploadsPreviewAudioDiscontinuityEnabled: boolean;
}) => {
  if (isLayoutWithVisionDataEnabledPure({ clip, isFullFaceRecEnabled, isFullFaceRecForPreRecordedEnabled })) {
    // With the audio-discontinuity fix, force the original video to be the sole
    // audio source (mute every per-speaker feed) so the audio doesn't break when
    // the active speaker/crop switches. See Use_CL_Uploads_Preview_Audio_Discontinuity.
    if (isUploadsPreviewAudioDiscontinuityEnabled) {
      return videos.filter(v => !v.isOriginalVideo).map(v => v.id);
    }

    // Default: only the active speaker (first video in the layout) is unmuted.
    const videoIdToPlay = videosInLayout[0];
    if (videoIdToPlay) {
      return videos.filter(v => v.id !== videoIdToPlay).map(v => v.id);
    }
    return videos.map(v => v.id);
  }

  let mutedVideosId: string[] = [];
  if (!doesClipWorkOnAlignedStreams(clip)) {
    mutedVideosId = videos.filter(v => !videosInLayout.includes(v.id)).map(v => v.id);
  }
  if (isLiveEventRemotionWithPreviewAndAlignedStream(clip)) {
    if (isCreatedFromOriginalLayout) {
      mutedVideosId = videos.filter(v => !v.isOriginalVideo).map(v => v.id);
    } else {
      mutedVideosId = videos.filter(v => v.isOriginalVideo).map(v => v.id);
    }
  }

  return mutedVideosId;
};
export const getLayoutChangesAndPlayRanges = ({
  clip,
  fps,
  layout = DEFAULT_LAYOUT_TYPE,
  size = DEFAULT_SIZE_TYPE,
  fitMode = DEFAULT_FIT_TYPE,
  hideBorders = DEFAULT_HIDE_BORDERS,
  roundCorners = false,
  pipShape,
  pipPosition,
  scenes,
  videos,
  videoFeeds,
  speakerAnalysis,
  isFullFaceRecEnabled,
  isFullFaceRecForPreRecordedEnabled,
  isScreenshareDetectionEnabled,
  isPersonContainerEnabled = false,
  isUploadsPreviewAudioDiscontinuityEnabled = false
}: Pick<LayoutChange, 'layout' | 'size' | 'fitMode' | 'hideBorders'> & {
  clip: Clip;
  fps: number;
  roundCorners?: boolean;
  pipShape?: LayoutChange['pipShape'];
  pipPosition?: LayoutChange['pipPosition'];
  scenes: Scene[];
  videos: VideoElement[];
  videoFeeds: VideoFeed[];
  speakerAnalysis?: SpeakersAnalysis;
  isFullFaceRecEnabled: boolean;
  isFullFaceRecForPreRecordedEnabled: boolean;
  isScreenshareDetectionEnabled: boolean;
  isPersonContainerEnabled?: boolean;
  isUploadsPreviewAudioDiscontinuityEnabled?: boolean;
}): { layoutChanges: LayoutChange[]; playRanges: PlayRanges } => {
  const speakerOverlaps = getSpeakerOverlaps({
    videoFeeds,
    clip,
    fps,
    scenes,
    speakerAnalysis,
    isFullFaceRecEnabled,
    isFullFaceRecForPreRecordedEnabled
  });
  let start = clip.asset_metadata.is_full_length ? 0 : clip.asset_metadata.start * fps;
  const end = clip.asset_metadata.end * fps;
  const isNewLayoutsEnabled = isNewLayoutsMigrated(clip);

  let flattenedDeletes = getFlattenedDeletes({ clipDeletes: clip.asset_metadata.deletes || {}, fps });
  if (flattenedDeletes.length > 0 && flattenedDeletes[0].fromFrame === 0) {
    start = flattenedDeletes[0].toFrame;
  }

  flattenedDeletes = flattenedDeletes.filter(deleteData => deleteData.fromFrame >= start && deleteData.toFrame <= end);

  const filteredSpeakerOverlaps = speakerOverlaps
    .filter(overlap => overlap.toFrame > start && overlap.fromFrame < end)
    .map(overlap => ({
      ...overlap,
      fromFrame: Math.max(overlap.fromFrame, start),
      toFrame: Math.min(overlap.toFrame, end)
    }));

  const shouldProcessFaceRecDataForLayouts = isLayoutWithVisionDataEnabledPure({
    clip,
    isFullFaceRecEnabled,
    isFullFaceRecForPreRecordedEnabled
  });

  const speakerDetailsMap = buildSpeakerDetailsMap(videoFeeds);

  let layoutChanges: LayoutChange[] = [];
  const playRanges: PlayRanges = [];
  for (const overlap of filteredSpeakerOverlaps) {
    let finalLayout = layout;
    let finalVideoIds: string[] = overlap.videoIds;
    let finalFitMode = fitMode;

    let isCreatedFromOriginalLayout = false;

    if (scenes.length > 0) {
      const sceneForOverlap = scenes.find(scene =>
        isApproxWindowSubset([overlap.fromFrame, overlap.toFrame], [scene.fromFrame, scene.toFrame])
      );

      if (sceneForOverlap) {
        finalLayout = sceneForOverlap.details.layout;
        finalFitMode = sceneForOverlap.details.fitMode;
        finalVideoIds = finalVideoIds.filter(id => sceneForOverlap.details.visible_video_ids.includes(id));
      }
    }

    if (isSpeakerLayout(finalLayout) && shouldProcessFaceRecDataForLayouts) {
      finalFitMode = 'FILL';
    }

    if (isLayoutAudiogram(finalLayout)) {
      finalVideoIds = removeScreenshareWithoutSpeakerVF({ videoFeeds, videoIds: finalVideoIds });
      if (finalVideoIds.length === 0 && layoutChanges.length > 0) {
        finalVideoIds = layoutChanges[layoutChanges.length - 1].videoIds;
      } else if (finalVideoIds.length > 0) {
        finalVideoIds = [finalVideoIds[0]];
      }
    } else if (isLayoutDefault(finalLayout)) {
      finalVideoIds = [finalVideoIds[0]];
      isCreatedFromOriginalLayout = true;
      finalLayout = 'GRID';
      finalFitMode = 'FIT';
    } else {
      // Old compiled engine: promote GRID/FOCUS to FOCUS when screenshare is present.
      // New runtime engine handles this mapping internally (GRID+ss→SHARED_FILL, FOCUS+ss→FILM_STRIP),
      // so the coercion must be skipped to keep LayoutChange.layout as the original author intent.
      if (!isNewLayoutsMigrated(clip) && overlap.hasScreenshare && !['SPEAKER', 'PIP'].includes(finalLayout)) {
        finalLayout = 'FOCUS';
      }

      if (!overlap.hasScreenshare) {
        finalVideoIds = removeScreenshareFromVideoIdsVF({ videoFeeds, videoIds: finalVideoIds });
      }

      if (finalLayout === 'PIP') {
        if (finalVideoIds.length >= 2) {
          if (overlap.hasScreenshare) {
            const screenshareVideoId = finalVideoIds.find(id => isVideoFeedScreenshare(videoFeeds, id));

            if (screenshareVideoId) {
              const fromFrame = overlap.fromFrame / fps;
              const toFrame = overlap.toFrame / fps;
              const activeSpeakers = getActiveSpeakersWithinTimeRange(fromFrame, toFrame, speakerDetailsMap);

              const nonScreenshareIds = finalVideoIds.filter(id => id !== screenshareVideoId);

              let activeSpeakerVideoId = nonScreenshareIds[0];
              if (activeSpeakers.length > 0) {
                const activeSpeakerId = activeSpeakers[0].speakerId;
                const activeSpeakerVideo = nonScreenshareIds.find(
                  id => getVideoFeedSpeakerKey(videoFeeds, id) === activeSpeakerId
                );
                if (activeSpeakerVideo) {
                  activeSpeakerVideoId = activeSpeakerVideo;
                }
              }

              finalVideoIds = [screenshareVideoId, activeSpeakerVideoId];
            }
          }
          if (!isNewLayoutsEnabled) {
            finalVideoIds = finalVideoIds.slice(0, 2);
          }
        } else {
          finalLayout = 'GRID';
        }
      }

      if (finalLayout === 'SPEAKER' || finalVideoIds.length === 1) {
        if (
          finalLayout === 'SPEAKER' &&
          shouldProcessFaceRecDataForLayouts &&
          finalVideoIds[0] !== SCREENSHARE_VIDEO_ID
        ) {
          const activeSpeakersForOverlap = getActiveSpeakersWithinTimeRange(
            overlap.fromFrame / fps,
            overlap.toFrame / fps,
            speakerDetailsMap
          );
          const firstVideoId = finalVideoIds[0];
          const firstVideoSpeakerKey = firstVideoId ? getVideoFeedSpeakerKey(videoFeeds, firstVideoId) : null;

          const activeSpeakerMatchesVideo =
            firstVideoSpeakerKey !== null && activeSpeakersForOverlap.some(s => s.speakerId === firstVideoSpeakerKey);

          if (!activeSpeakerMatchesVideo) {
            const originalVideo = videos.find(v => v.isOriginalVideo);
            finalVideoIds = originalVideo ? [originalVideo.id] : finalVideoIds.length > 0 ? [finalVideoIds[0]] : [];
            isCreatedFromOriginalLayout = true;
            finalFitMode = 'FIT';
          } else {
            finalVideoIds = finalVideoIds.length > 0 ? [finalVideoIds[0]] : [];
          }
        } else {
          finalVideoIds = finalVideoIds.length > 0 ? [finalVideoIds[0]] : [];
        }
        finalLayout = 'GRID';
      }
    }

    const deletesCreepingIntoRange = flattenedDeletes.filter(
      deleteData => deleteData.toFrame > overlap.fromFrame && deleteData.fromFrame < overlap.toFrame
    );

    let overlaps: { fromFrame: number; toFrame: number; hasScreenshare: boolean }[] = [
      {
        fromFrame: overlap.fromFrame,
        toFrame: overlap.toFrame,
        hasScreenshare: overlap.hasScreenshare
      }
    ];

    if (deletesCreepingIntoRange.length > 0) {
      const availableRanges = getAvailableRanges([overlap.fromFrame, overlap.toFrame], deletesCreepingIntoRange);
      overlaps = availableRanges.map(range => ({
        fromFrame: range[0],
        toFrame: range[1],
        hasScreenshare: overlap.hasScreenshare
      }));
    }

    overlaps.forEach(overlap => {
      let videoCropPositionMap: Record<string, VideoCropPosition> | undefined;
      let videoIds: string[] = finalVideoIds;
      let layoutForOverlap: LayoutEnum = finalLayout;
      const faceCoordinatesMap: Record<string, VideoCropPosition> = {};
      const personContainerCoordinatesMap: Record<string, VideoCropPosition> = {};
      const videoIsUsingManualRepositioning: Record<string, boolean> = {};

      if (scenes.length > 0) {
        const sceneForOverlap = scenes.find(scene =>
          isApproxWindowSubset([overlap.fromFrame, overlap.toFrame], [scene.fromFrame, scene.toFrame])
        );

        if (sceneForOverlap) {
          if (sceneForOverlap.details.video_crop_position_map) {
            videoCropPositionMap = sceneForOverlap.details.video_crop_position_map;
          }

          const sceneLayout = sceneForOverlap.details.layout;
          const shouldFetchCropPositions = isSpeakerLayout(sceneLayout);

          if (shouldFetchCropPositions && shouldProcessFaceRecDataForLayouts) {
            const cropPositionsResult = getCropPositionsForUploadsVF({
              clip,
              overlap,
              fps,
              finalLayout,
              fitMode,
              hideBorders,
              finalVideoIds,
              videos,
              hasVisibleVideoIds: sceneForOverlap.details.visible_video_ids.length > 0,
              videoFeeds,
              speakerAnalysis,
              isScreenshareDetectionEnabled,
              roundCorners,
              pipShape,
              pipPosition,
              isNewLayoutsEnabled
            });

            if (isScreenshareDetectionEnabled && cropPositionsResult?.videoCropPositionMap?.[SCREENSHARE_VIDEO_ID]) {
              videoIsUsingManualRepositioning[SCREENSHARE_VIDEO_ID] = true;
            }

            if (cropPositionsResult) {
              videoIds = cropPositionsResult.videoIds;
              layoutForOverlap = cropPositionsResult.layoutForOverlap;
              Object.assign(faceCoordinatesMap, cropPositionsResult.faceCoordinatesMap);
              Object.assign(personContainerCoordinatesMap, cropPositionsResult.personContainerCoordinatesMap);

              if (videoCropPositionMap) {
                Object.keys(videoCropPositionMap).forEach(videoId => {
                  videoIsUsingManualRepositioning[videoId] = true;
                });
              }
              videoCropPositionMap = { ...cropPositionsResult.videoCropPositionMap, ...videoCropPositionMap };
            }
          } else if (videoCropPositionMap && Object.keys(videoCropPositionMap).length > 0) {
            Object.keys(videoCropPositionMap).forEach(key => {
              videoIsUsingManualRepositioning[key] = true;
            });
          }
        }
      } else if (scenes.length === 0) {
        const shouldFetchCropPositions = isSpeakerLayout(finalLayout);

        if (shouldFetchCropPositions && shouldProcessFaceRecDataForLayouts) {
          const visibleVideoIds = clip.asset_metadata.visible_video_ids || finalVideoIds;
          const filteredVideoIds = finalVideoIds.filter(videoId => visibleVideoIds.includes(videoId));
          const cropPositionsResult = getCropPositionsForUploadsVF({
            clip,
            overlap,
            fps,
            finalLayout,
            fitMode,
            hideBorders,
            finalVideoIds: filteredVideoIds,
            videos,
            hasVisibleVideoIds: filteredVideoIds.length > 0,
            videoFeeds,
            speakerAnalysis,
            isScreenshareDetectionEnabled,
            roundCorners,
            pipShape,
            pipPosition,
            isNewLayoutsEnabled
          });

          if (cropPositionsResult) {
            videoIds = cropPositionsResult.videoIds;
            layoutForOverlap = cropPositionsResult.layoutForOverlap;
            Object.assign(faceCoordinatesMap, cropPositionsResult.faceCoordinatesMap);
            Object.assign(personContainerCoordinatesMap, cropPositionsResult.personContainerCoordinatesMap);
            videoCropPositionMap = cropPositionsResult.videoCropPositionMap;
          }
        }
      }

      const layoutDetailsAtCurrentFrame = {
        fromFrame: getAdjustedFrame(overlap.fromFrame, flattenedDeletes, start),
        toFrame: getAdjustedFrame(overlap.toFrame, flattenedDeletes, start),
        videoIds,
        layout: layoutForOverlap,
        size,
        fitMode: finalFitMode,
        hideBorders,
        roundCorners,
        pipShape,
        pipPosition,
        hasScreenshare: overlap.hasScreenshare,
        mutedVideosId: getMutedVideoIdsVF({
          clip,
          videosInLayout: videoIds,
          videos,
          isCreatedFromOriginalLayout,
          isFullFaceRecEnabled,
          isFullFaceRecForPreRecordedEnabled,
          isUploadsPreviewAudioDiscontinuityEnabled
        }),
        videoCropPositionMap,
        speakerDirection: clip.asset_metadata.speaker_direction,
        useNewLayoutEngine: isNewLayoutsEnabled
      };
      const finalConfig = getFinalConfigForLayout(layoutDetailsAtCurrentFrame, videoIds);
      const videoCropPositionStyles: Record<string, VideoCropPositionStyle> = {};
      const preventOverlappingWithOtherCrops = shouldProcessFaceRecDataForLayouts;
      videoIds.forEach((videoId, index) => {
        const videoCropPosition = videoCropPositionMap?.[videoId];
        const isUploadAndUsingManualRepositioned =
          shouldProcessFaceRecDataForLayouts && videoIsUsingManualRepositioning[videoId];
        if (!videoCropPosition || !finalConfig[index]) {
          return;
        }
        const videoAspectRatio = videos.find(v => v.id === videoId)?.aspectRatio;
        if (videoId === SCREENSHARE_VIDEO_ID && shouldProcessFaceRecDataForLayouts) {
          videoCropPositionStyles[videoId] = getFitCropStyles(
            videoCropPosition,
            finalConfig[index].containerStyle as CSSProperties,
            getAspectRatio(clip.asset_metadata.size),
            videoAspectRatio
          );
          return;
        }

        const isManualRepositioned = isUploadAndUsingManualRepositioned || !shouldProcessFaceRecDataForLayouts;
        const allOtherCropInformation = preventOverlappingWithOtherCrops
          ? {
              ...Object.keys(faceCoordinatesMap || {})
                .filter(key => key !== videoId)
                .reduce<Record<string, VideoCropPosition>>((acc, key) => {
                  acc[key] = faceCoordinatesMap[key];
                  return acc;
                }, {}),
              ...Object.keys(videoCropPositionMap || {})
                .filter(key => key !== videoId)
                .reduce<Record<string, VideoCropPosition>>((acc, key) => {
                  const cropPosition = videoCropPositionMap?.[key];
                  if (cropPosition) {
                    acc[key] = cropPosition;
                  }
                  return acc;
                }, {})
            }
          : undefined;

        videoCropPositionStyles[videoId] = getCropStyles(
          videoCropPosition,
          finalConfig[index].containerStyle as CSSProperties,
          getAspectRatio(clip.asset_metadata.size),
          allOtherCropInformation,
          isManualRepositioned,
          videoAspectRatio,
          faceCoordinatesMap?.[videoId],
          isPersonContainerEnabled && shouldProcessFaceRecDataForLayouts && !isManualRepositioned
            ? personContainerCoordinatesMap[videoId]
            : undefined
        );
      });

      layoutChanges.push({
        ...layoutDetailsAtCurrentFrame,
        videoCropPositionStyles
      });
      playRanges.push({
        fromFrame: overlap.fromFrame,
        toFrame: overlap.toFrame
      });
    });
  }

  // Merge away negligible-duration layout changes so the player never resolves them.
  layoutChanges = mergeNegligibleLayoutChanges(layoutChanges);

  if (playRanges.length && playRanges[0].fromFrame !== 0 && start === 0) {
    playRanges.unshift({
      fromFrame: 0,
      toFrame: playRanges[0].fromFrame
    });
  }

  if (
    (isClipLayoutDefault(clip) || isClipAudiogram(clip)) &&
    layoutChanges.length > 0 &&
    layoutChanges[0].fromFrame !== 0
  ) {
    const firstLayout = layoutChanges[0];
    layoutChanges.unshift({
      ...firstLayout,
      fromFrame: 0,
      toFrame: firstLayout.fromFrame
    });
  }

  if (playRanges.length && playRanges[playRanges.length - 1].toFrame < end) {
    playRanges.push({
      fromFrame: playRanges[playRanges.length - 1].toFrame,
      toFrame: end
    });
  }

  if (
    (isClipLayoutDefault(clip) || isClipAudiogram(clip)) &&
    layoutChanges.length > 0 &&
    layoutChanges[layoutChanges.length - 1].toFrame < end
  ) {
    const lastLayout = layoutChanges[layoutChanges.length - 1];
    layoutChanges.push({ ...lastLayout, fromFrame: lastLayout.fromFrame, toFrame: end });
  }

  // Merge away negligible-duration play ranges so <Series.Sequence> never gets a zero/epsilon duration.
  return { layoutChanges, playRanges: mergeNegligiblePlayRanges(playRanges) };
};
export const getLayoutChangesAndPlayRangesWithSplits = ({
  clip,
  fps,
  layout = DEFAULT_LAYOUT_TYPE,
  size = DEFAULT_SIZE_TYPE,
  fitMode = DEFAULT_FIT_TYPE,
  hideBorders = DEFAULT_HIDE_BORDERS,
  roundCorners = false,
  pipShape,
  pipPosition,
  scenes,
  videos,
  oldSegment,
  videoFeeds,
  speakerAnalysis,
  isScreenshareDetectionEnabled,
  isPersonContainerEnabled = false,
  isFullFaceRecEnabled,
  isFullFaceRecForPreRecordedEnabled,
  isUploadsPreviewAudioDiscontinuityEnabled = false
}: Pick<LayoutChange, 'layout' | 'size' | 'fitMode' | 'hideBorders'> & {
  clip: Clip;
  fps: number;
  roundCorners?: boolean;
  pipShape?: LayoutChange['pipShape'];
  pipPosition?: LayoutChange['pipPosition'];
  scenes: Scene[];
  videos: VideoElement[];
  oldSegment: CompositeSegment;
  videoFeeds: VideoFeed[];
  speakerAnalysis?: SpeakersAnalysis;
  isScreenshareDetectionEnabled: boolean;
  isPersonContainerEnabled?: boolean;
  isFullFaceRecEnabled: boolean;
  isFullFaceRecForPreRecordedEnabled: boolean;
  isUploadsPreviewAudioDiscontinuityEnabled?: boolean;
}): { layoutChanges: LayoutChange[]; playRanges: PlayRanges } => {
  const segmentMetadata = oldSegment?.segment_metadata;
  if (!segmentMetadata) {
    return { layoutChanges: [], playRanges: [] };
  }

  const speakerOverlaps = getSpeakerOverlaps({
    videoFeeds,
    clip,
    fps,
    scenes,
    speakerAnalysis,
    isFullFaceRecEnabled,
    isFullFaceRecForPreRecordedEnabled
  });
  let start = clip.asset_metadata.is_full_length ? 0 : segmentMetadata.start * fps;
  const end = segmentMetadata.end * fps;

  let flattenedDeletes = getFlattenedDeletes({ clipDeletes: segmentMetadata.deletes || {}, fps });
  if (flattenedDeletes.length > 0 && flattenedDeletes[0].fromFrame === 0) {
    start = flattenedDeletes[0].toFrame;
  }

  flattenedDeletes = flattenedDeletes.filter(deleteData => deleteData.fromFrame >= start && deleteData.toFrame <= end);

  const filteredSpeakerOverlaps = speakerOverlaps
    .filter(overlap => overlap.toFrame > start && overlap.fromFrame < end)
    .map(overlap => ({
      ...overlap,
      fromFrame: Math.max(overlap.fromFrame, start),
      toFrame: Math.min(overlap.toFrame, end)
    }));

  const shouldProcessFaceRecDataForLayouts = isLayoutWithVisionDataEnabledPure({
    clip,
    isFullFaceRecEnabled,
    isFullFaceRecForPreRecordedEnabled
  });

  const speakerDetailsMap = buildSpeakerDetailsMap(videoFeeds);
  const isNewLayoutsEnabled = isNewLayoutsMigrated(clip);
  let layoutChanges: LayoutChange[] = [];
  const playRanges: PlayRanges = [];
  for (const overlap of filteredSpeakerOverlaps) {
    let finalLayout = layout;
    let finalVideoIds: string[] = overlap.videoIds;
    let finalFitMode = fitMode;

    let isCreatedFromOriginalLayout = false;

    if (scenes.length > 0) {
      const sceneForOverlap = scenes.find(scene =>
        isApproxWindowSubset([overlap.fromFrame, overlap.toFrame], [scene.fromFrame, scene.toFrame])
      );

      if (sceneForOverlap) {
        finalLayout = sceneForOverlap.details.layout;
        finalFitMode = sceneForOverlap.details.fitMode;
        finalVideoIds = finalVideoIds.filter(id => sceneForOverlap.details.visible_video_ids.includes(id));
      }
    }

    if (isSpeakerLayout(finalLayout) && shouldProcessFaceRecDataForLayouts) {
      finalFitMode = 'FILL';
    }

    if (isLayoutAudiogram(finalLayout)) {
      finalVideoIds = removeScreenshareWithoutSpeakerVF({ videoFeeds, videoIds: finalVideoIds });
      if (finalVideoIds.length === 0 && layoutChanges.length > 0) {
        finalVideoIds = layoutChanges[layoutChanges.length - 1].videoIds;
      } else if (finalVideoIds.length > 0) {
        finalVideoIds = [finalVideoIds[0]];
      }
    } else if (isLayoutDefault(finalLayout)) {
      finalVideoIds = [finalVideoIds[0]];
      isCreatedFromOriginalLayout = true;
      finalLayout = 'GRID';
      finalFitMode = 'FIT';
    } else {
      // Old compiled engine: promote GRID/FOCUS to FOCUS when screenshare is present.
      // New runtime engine handles this mapping internally (GRID+ss→SHARED_FILL, FOCUS+ss→FILM_STRIP).
      if (!isNewLayoutsMigrated(clip) && overlap.hasScreenshare && !['SPEAKER', 'PIP'].includes(finalLayout)) {
        finalLayout = 'FOCUS';
      }

      if (!overlap.hasScreenshare) {
        finalVideoIds = removeScreenshareFromVideoIdsVF({ videoFeeds, videoIds: finalVideoIds });
      }

      if (finalLayout === 'PIP') {
        if (finalVideoIds.length >= 2) {
          if (overlap.hasScreenshare) {
            const screenshareVideoId = finalVideoIds.find(id => isVideoFeedScreenshare(videoFeeds, id));
            if (screenshareVideoId) {
              const fromFrame = overlap.fromFrame / fps;
              const toFrame = overlap.toFrame / fps;
              const activeSpeakers = getActiveSpeakersWithinTimeRange(fromFrame, toFrame, speakerDetailsMap);
              const nonScreenshareIds = finalVideoIds.filter(id => id !== screenshareVideoId);
              let activeSpeakerVideoId = nonScreenshareIds[0];
              if (activeSpeakers.length > 0) {
                const activeSpeakerId = activeSpeakers[0].speakerId;
                const activeSpeakerVideo = nonScreenshareIds.find(
                  id => getVideoFeedSpeakerKey(videoFeeds, id) === activeSpeakerId
                );
                if (activeSpeakerVideo) {
                  activeSpeakerVideoId = activeSpeakerVideo;
                }
              }
              finalVideoIds = [screenshareVideoId, activeSpeakerVideoId];
            }
          }
          if (!isNewLayoutsEnabled) {
            finalVideoIds = finalVideoIds.slice(0, 2);
          }
        } else {
          finalLayout = 'GRID';
        }
      }

      if (finalLayout === 'SPEAKER' || finalVideoIds.length === 1) {
        if (
          finalLayout === 'SPEAKER' &&
          shouldProcessFaceRecDataForLayouts &&
          finalVideoIds[0] !== SCREENSHARE_VIDEO_ID
        ) {
          const activeSpeakersForOverlap = getActiveSpeakersWithinTimeRange(
            overlap.fromFrame / fps,
            overlap.toFrame / fps,
            speakerDetailsMap
          );
          const firstVideoId = finalVideoIds[0];
          const firstVideoSpeakerKey = firstVideoId ? getVideoFeedSpeakerKey(videoFeeds, firstVideoId) : null;
          const activeSpeakerMatchesVideo =
            firstVideoSpeakerKey !== null && activeSpeakersForOverlap.some(s => s.speakerId === firstVideoSpeakerKey);

          if (!activeSpeakerMatchesVideo) {
            const originalVideo = videos.find(v => v.isOriginalVideo);
            finalVideoIds = originalVideo ? [originalVideo.id] : finalVideoIds.length > 0 ? [finalVideoIds[0]] : [];
            isCreatedFromOriginalLayout = true;
            finalFitMode = 'FIT';
          } else {
            finalVideoIds = finalVideoIds.length > 0 ? [finalVideoIds[0]] : [];
          }
        } else {
          finalVideoIds = finalVideoIds.length > 0 ? [finalVideoIds[0]] : [];
        }
        finalLayout = 'GRID';
      }
    }

    const deletesCreepingIntoRange = flattenedDeletes.filter(
      deleteData => deleteData.toFrame > overlap.fromFrame && deleteData.fromFrame < overlap.toFrame
    );

    let overlaps: { fromFrame: number; toFrame: number; hasScreenshare: boolean }[] = [
      {
        fromFrame: overlap.fromFrame,
        toFrame: overlap.toFrame,
        hasScreenshare: overlap.hasScreenshare
      }
    ];

    if (deletesCreepingIntoRange.length > 0) {
      const availableRanges = getAvailableRanges([overlap.fromFrame, overlap.toFrame], deletesCreepingIntoRange);
      overlaps = availableRanges.map(range => ({
        fromFrame: range[0],
        toFrame: range[1],
        hasScreenshare: overlap.hasScreenshare
      }));
    }

    overlaps.forEach(overlap => {
      let videoCropPositionMap: Record<string, VideoCropPosition> | undefined;
      let videoIds: string[] = finalVideoIds;
      let layoutForOverlap: LayoutEnum = finalLayout;
      const faceCoordinatesMap: Record<string, VideoCropPosition> = {};
      const personContainerCoordinatesMap: Record<string, VideoCropPosition> = {};
      const videoIsUsingManualRepositioning: Record<string, boolean> = {};

      if (scenes.length > 0) {
        const sceneForOverlap = scenes.find(
          scene => overlap.fromFrame >= scene.fromFrame && overlap.toFrame <= scene.toFrame
        );

        if (sceneForOverlap) {
          if (sceneForOverlap.details.video_crop_position_map) {
            videoCropPositionMap = sceneForOverlap.details.video_crop_position_map;
          }

          const sceneLayout = sceneForOverlap.details.layout;
          const shouldFetchCropPositions = isSpeakerLayout(sceneLayout);

          if (shouldFetchCropPositions && shouldProcessFaceRecDataForLayouts) {
            const cropPositionsResult = getCropPositionsForUploadsVF({
              clip,
              overlap,
              fps,
              finalLayout,
              fitMode,
              hideBorders,
              finalVideoIds,
              videos,
              hasVisibleVideoIds: sceneForOverlap.details.visible_video_ids.length > 0,
              videoFeeds,
              speakerAnalysis,
              isScreenshareDetectionEnabled,
              roundCorners,
              pipShape,
              pipPosition,
              isNewLayoutsEnabled
            });

            if (isScreenshareDetectionEnabled && cropPositionsResult?.videoCropPositionMap?.[SCREENSHARE_VIDEO_ID]) {
              videoIsUsingManualRepositioning[SCREENSHARE_VIDEO_ID] = true;
            }

            if (cropPositionsResult) {
              videoIds = cropPositionsResult.videoIds;
              layoutForOverlap = cropPositionsResult.layoutForOverlap;
              Object.assign(faceCoordinatesMap, cropPositionsResult.faceCoordinatesMap);
              Object.assign(personContainerCoordinatesMap, cropPositionsResult.personContainerCoordinatesMap);

              if (videoCropPositionMap) {
                Object.keys(videoCropPositionMap).forEach(videoId => {
                  videoIsUsingManualRepositioning[videoId] = true;
                });
              }
              videoCropPositionMap = { ...cropPositionsResult.videoCropPositionMap, ...videoCropPositionMap };
            }
          } else if (videoCropPositionMap && Object.keys(videoCropPositionMap).length > 0) {
            Object.keys(videoCropPositionMap).forEach(key => {
              videoIsUsingManualRepositioning[key] = true;
            });
          }
        }
      } else if (scenes.length === 0) {
        const shouldFetchCropPositions = isSpeakerLayout(finalLayout);

        if (shouldFetchCropPositions && shouldProcessFaceRecDataForLayouts) {
          const visibleVideoIds = clip.asset_metadata.visible_video_ids || finalVideoIds;
          const filteredVideoIds = finalVideoIds.filter(videoId => visibleVideoIds.includes(videoId));
          const cropPositionsResult = getCropPositionsForUploadsVF({
            clip,
            overlap,
            fps,
            finalLayout,
            fitMode,
            hideBorders,
            finalVideoIds: filteredVideoIds,
            videos,
            hasVisibleVideoIds: filteredVideoIds.length > 0,
            videoFeeds,
            speakerAnalysis,
            isScreenshareDetectionEnabled,
            roundCorners,
            pipShape,
            pipPosition,
            isNewLayoutsEnabled
          });

          if (cropPositionsResult) {
            videoIds = cropPositionsResult.videoIds;
            layoutForOverlap = cropPositionsResult.layoutForOverlap;
            Object.assign(faceCoordinatesMap, cropPositionsResult.faceCoordinatesMap);
            Object.assign(personContainerCoordinatesMap, cropPositionsResult.personContainerCoordinatesMap);
            videoCropPositionMap = cropPositionsResult.videoCropPositionMap;
          }
        }
      }

      const layoutDetailsAtCurrentFrame = {
        fromFrame: getAdjustedFrame(overlap.fromFrame, flattenedDeletes, start),
        toFrame: getAdjustedFrame(overlap.toFrame, flattenedDeletes, start),
        videoIds,
        layout: layoutForOverlap,
        size,
        fitMode: finalFitMode,
        hideBorders,
        roundCorners,
        pipShape,
        pipPosition,
        hasScreenshare: overlap.hasScreenshare,
        mutedVideosId: getMutedVideoIdsVF({
          clip,
          videosInLayout: videoIds,
          videos,
          isCreatedFromOriginalLayout,
          isFullFaceRecEnabled,
          isFullFaceRecForPreRecordedEnabled,
          isUploadsPreviewAudioDiscontinuityEnabled
        }),
        videoCropPositionMap,
        speakerDirection: isNewLayoutsEnabled ? clip.asset_metadata.speaker_direction : undefined,
        useNewLayoutEngine: isNewLayoutsEnabled
      };
      const finalConfig = getFinalConfigForLayout(layoutDetailsAtCurrentFrame, videoIds);
      const videoCropPositionStyles: Record<string, VideoCropPositionStyle> = {};
      const preventOverlappingWithOtherCrops = shouldProcessFaceRecDataForLayouts;
      videoIds.forEach((videoId, index) => {
        const videoCropPosition = videoCropPositionMap?.[videoId];
        const isUploadAndUsingManualRepositioned =
          shouldProcessFaceRecDataForLayouts && videoIsUsingManualRepositioning[videoId];
        if (!videoCropPosition || !finalConfig[index]) {
          return;
        }
        const videoAspectRatio = videos.find(v => v.id === videoId)?.aspectRatio;
        if (videoId === SCREENSHARE_VIDEO_ID && shouldProcessFaceRecDataForLayouts) {
          videoCropPositionStyles[videoId] = getFitCropStyles(
            videoCropPosition,
            finalConfig[index].containerStyle as CSSProperties,
            getAspectRatio(clip.asset_metadata.size),
            videoAspectRatio
          );
          return;
        }

        const isManualRepositioned = isUploadAndUsingManualRepositioned || !shouldProcessFaceRecDataForLayouts;
        const allOtherCropInformation = preventOverlappingWithOtherCrops
          ? {
              ...Object.keys(faceCoordinatesMap || {})
                .filter(key => key !== videoId)
                .reduce<Record<string, VideoCropPosition>>((acc, key) => {
                  acc[key] = faceCoordinatesMap[key];
                  return acc;
                }, {}),
              ...Object.keys(videoCropPositionMap || {})
                .filter(key => key !== videoId)
                .reduce<Record<string, VideoCropPosition>>((acc, key) => {
                  const cropPosition = videoCropPositionMap?.[key];
                  if (cropPosition) {
                    acc[key] = cropPosition;
                  }
                  return acc;
                }, {})
            }
          : undefined;

        videoCropPositionStyles[videoId] = getCropStyles(
          videoCropPosition,
          finalConfig[index].containerStyle as CSSProperties,
          getAspectRatio(clip.asset_metadata.size),
          allOtherCropInformation,
          isManualRepositioned,
          videoAspectRatio,
          faceCoordinatesMap?.[videoId],
          isPersonContainerEnabled && shouldProcessFaceRecDataForLayouts && !isManualRepositioned
            ? personContainerCoordinatesMap[videoId]
            : undefined
        );
      });

      layoutChanges.push({
        ...layoutDetailsAtCurrentFrame,
        videoCropPositionStyles
      });
      playRanges.push({
        fromFrame: overlap.fromFrame,
        toFrame: overlap.toFrame
      });
    });
  }

  // Merge away negligible-duration layout changes so the player never resolves them.
  layoutChanges = mergeNegligibleLayoutChanges(layoutChanges);

  if (playRanges.length && playRanges[0].fromFrame !== 0 && start === 0) {
    playRanges.unshift({
      fromFrame: 0,
      toFrame: playRanges[0].fromFrame
    });
  }

  if (
    (isClipLayoutDefault(clip) || isClipAudiogram(clip)) &&
    layoutChanges.length > 0 &&
    layoutChanges[0].fromFrame !== 0
  ) {
    const firstLayout = layoutChanges[0];
    layoutChanges.unshift({
      ...firstLayout,
      fromFrame: 0,
      toFrame: firstLayout.fromFrame
    });
  }

  if (playRanges.length && playRanges[playRanges.length - 1].toFrame < end) {
    playRanges.push({
      fromFrame: playRanges[playRanges.length - 1].toFrame,
      toFrame: end
    });
  }

  if (
    (isClipLayoutDefault(clip) || isClipAudiogram(clip)) &&
    layoutChanges.length > 0 &&
    layoutChanges[layoutChanges.length - 1].toFrame < end
  ) {
    const lastLayout = layoutChanges[layoutChanges.length - 1];
    layoutChanges.push({ ...lastLayout, fromFrame: lastLayout.fromFrame, toFrame: end });
  }

  // Merge away negligible-duration play ranges so <Series.Sequence> never gets a zero/epsilon duration.
  return { layoutChanges, playRanges: mergeNegligiblePlayRanges(playRanges) };
};
